import { useEffect, useState, useMemo, useCallback } from 'react';
import { useGiftWrapContext } from '../../context/GiftWrapContext';

export function useViewModel({ parentId, onScrollToElement, firstOptionRef, optionsRef }) {
  const {
    giftInfo,
    activePersonalisation,
    setActivePersonalisation,
    wrapOptions,
    toggleGiftWrapSelection,
    isGiftWrapItemSelected,
    updatePersonalisationChoice,
    updateInputState,
    inputStates
  } = useGiftWrapContext();

  // Get input state from context instead of local state - make it reactive
  const inputState = useMemo(() => {
    return (
      inputStates[`${parentId}`] || {
        isExpanded: false,
        addPersonalisation: false,
        addWaxStamp: false,
        personalisationText: '',
        selectedDesign: '',
        giftMessage: ''
      }
    );
  }, [inputStates, parentId]);
  const { isExpanded, addPersonalisation, addWaxStamp, personalisationText, selectedDesign } = inputState;
  const [hasPersonalisationText, setHasPersonalisationText] = useState(false);
  const [hasWaxStamp, setHasWaxStamp] = useState(false);

  // Check for personalization options for this specific item
  useEffect(() => {
    const itemWrapOption = wrapOptions.find((o) => o.product_id === parentId);
    if (itemWrapOption?.variants) {
      const variants = itemWrapOption.variants || [];
      const havePT = variants.some((o) => {
        const t = o.title?.toLowerCase() || '';
        return t.includes('custom text') || t.includes('add a name');
      });
      setHasPersonalisationText(havePT);
    }

    // Check if wax stamps are available for this item type
    // Wax stamps are separate products, not variants of the gift wrap item
    const itemType = itemWrapOption?.type;
    if (itemType === 'bag' || itemType === 'box') {
      const stampType = itemType === 'bag' ? 'stamp_bag' : 'stamp_box';
      const hasStamps = wrapOptions.some((option) => option.type === stampType);
      setHasWaxStamp(hasStamps);
    } else {
      setHasWaxStamp(false);
    }
  }, [wrapOptions, parentId]);

  // Handle activePersonalisation changes (for backward compatibility)
  useEffect(() => {
    const fullActivePersonalisation = wrapOptions.find((o) => o.product_id === activePersonalisation?.parentId);
    if (fullActivePersonalisation) {
      // Only expand if this is the active item, but don't auto-close other items
      if (parentId === activePersonalisation?.parentId) {
        updateInputState(parentId, { isExpanded: true });
      }
      // Removed auto-close logic - items should stay open when selected
    }
  }, [activePersonalisation, parentId, updateInputState, wrapOptions]);

  // Calculate selection state directly using useMemo
  const isSelected = useMemo(() => {
    const isCurrentlySelected = isGiftWrapItemSelected(parentId);
    // Only log when selection state changes
    return isCurrentlySelected;
  }, [parentId, isGiftWrapItemSelected]);

  // Auto-expand when item is selected (separate from activePersonalisation logic)
  useEffect(() => {
    if (isSelected) {
      updateInputState(parentId, { isExpanded: true });
    }
  }, [isSelected, parentId, updateInputState]);

  // Scroll to personalization input when it becomes enabled
  useEffect(() => {
    if (addPersonalisation && onScrollToElement && firstOptionRef?.current) {
      setTimeout(() => {
        const textInput = firstOptionRef.current.querySelector('input[type="text"]');
        if (textInput) {
          onScrollToElement(textInput);
        } else {
          onScrollToElement(firstOptionRef.current);
        }
      }, 200); // Short delay for DOM update
    }
  }, [addPersonalisation, onScrollToElement, firstOptionRef]);

  const onChangePersonalisationText = (value) => {
    const fullActivePersonalisation = wrapOptions.find((o) => o.product_id === activePersonalisation?.parentId);
    if (fullActivePersonalisation) {
      const variant = fullActivePersonalisation.variants.find((o) => {
        const title = o.title?.toLowerCase() || '';
        if (title.includes('custom text') || title.includes('add a name')) {
          return o.id;
        }
      });

      // Update input state in context
      updateInputState(parentId, { personalisationText: value });

      // Update both activePersonalisation and personalisationChoices
      setActivePersonalisation({
        ...activePersonalisation,
        text: value,
        vId_gift: variant?.id ?? '',
        title: variant?.title ?? '',
        price: String(variant?.price || 0)
      });

      // Update the context choice
      updatePersonalisationChoice(parentId, {
        text: value,
        vId_gift: variant?.id ?? '',
        title: variant?.title ?? '',
        price: String(variant?.price || 0)
      });
    }
  };

  const onChangeWasStamp = (value) => {
    // Get the current item's type to determine which stamp to look for
    const itemWrapOption = wrapOptions.find((o) => o.product_id === parentId);
    const itemType = itemWrapOption?.type;

    let stamp;
    if (itemType === 'bag') {
      stamp = wrapOptions.find((o) => o.type === 'stamp_bag');
    } else if (itemType === 'box') {
      stamp = wrapOptions.find((o) => o.type === 'stamp_box');
    }

    if (Array.isArray(stamp)) {
      stamp = stamp[0];
    }
    const stampLabel = stamp?.variants?.find((v) => v.id === value)?.title || '';

    // Update input state in context
    updateInputState(parentId, { selectedDesign: value });

    // Update both activePersonalisation and personalisationChoices
    setActivePersonalisation({
      ...activePersonalisation,
      vId_stamp: value,
      vId_stamp_label: stampLabel
    });

    // Update the context choice
    updatePersonalisationChoice(parentId, {
      vId_stamp: value,
      vId_stamp_label: stampLabel
    });
  };

  const handleToggleSelection = useCallback(() => {
    // Find the wrap option for this parentId
    const wrapOption = wrapOptions.find((o) => o.product_id === parentId);

    if (wrapOption) {
      // Use centralized context function to toggle selection
      const wasExpanded = isExpanded;
      toggleGiftWrapSelection(parentId, wrapOption);

      // If expanding and we have a scroll function, scroll to center the first option
      if (!wasExpanded && onScrollToElement) {
        // Use setTimeout to ensure the DOM has updated with the expanded content
        setTimeout(() => {
          // Try to find the best element to scroll to
          let elementToScroll = null;

          // First, try to find a text input within the first option
          if (firstOptionRef?.current) {
            const textInput = firstOptionRef.current.querySelector('input[type="text"], textarea');
            if (textInput) {
              elementToScroll = textInput;
            } else {
              elementToScroll = firstOptionRef.current;
            }
          }

          // Fallback to options container
          if (!elementToScroll) {
            elementToScroll = optionsRef?.current;
          }

          // If still no element, try to find by CSS selector as last resort
          if (!elementToScroll) {
            // Use a more React-friendly approach - look for the parent container
            const giftWrapContent = document.querySelector('[class*="giftWrap__content"]');
            if (giftWrapContent) {
              // Look for the most recently expanded options container
              const optionsContainer = giftWrapContent.querySelector('[class*="giftBag__options"]:last-of-type');
              if (optionsContainer) {
                elementToScroll = optionsContainer;
              }
            }
          }

          if (elementToScroll) {
            onScrollToElement(elementToScroll);
          } else {
          }
        }, 500);
      } else {
      }
    }
  }, [parentId, wrapOptions, toggleGiftWrapSelection, isExpanded, onScrollToElement, firstOptionRef, optionsRef]);

  return {
    isExpanded,
    setIsExpanded: (value) => updateInputState(parentId, { isExpanded: value }),
    addPersonalisation,
    setAddPersonalisation: (value) => updateInputState(parentId, { addPersonalisation: value }),
    addWaxStamp,
    setAddWaxStamp: (value) => updateInputState(parentId, { addWaxStamp: value }),
    personalisationText,
    onChangePersonalisationText,
    selectedDesign,
    onChangeWasStamp,
    giftInfo,
    hasPersonalisationText,
    hasWaxStamp,
    handleToggleSelection,
    isSelected
  };
}
